import { useTranslation } from 'react-i18next'
import Icon from './Icon'

/**
 * Web mirror of mobile's ChallengePipeline. Visualises the 4-step challenge
 * lifecycle and highlights the viewer's current step. See the mobile
 * component (apps/mobile/src/features/challenge/ChallengePipeline.tsx) for
 * the full design rationale.
 */

const STEPS_LOCAL         = ['accept', 'date',  'meet',    'wrap']
const STEPS_INTERNATIONAL = ['accept', 'proof', 'verdict']
const ICONS  = { accept: 'people-outline', date: 'calendar-outline', meet: 'hand-left-outline', wrap: 'sparkles-outline', proof: 'camera-outline', verdict: 'shield-checkmark-outline' }

// Compact "sam. 6 juin · 21:30" - locale-aware via Intl using the active
// i18n language (NOT undefined, which falls back to the device locale and
// reads English even for French-speaking users).
function formatMeetupDate(unixSeconds, locale) {
  const d = new Date(unixSeconds * 1000)
  const day  = d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })
  const time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
  return `${day} · ${time}`
}

function derive(acceptance, iAmCreator, myUserId, locale, usesPhotoProof) {
  if (!acceptance) {
    // Visitors don't get a sub-CTA here - the participants row below has the
    // labeled "Take on the challenge" button. Two prompts read as a repeat.
    return {
      active: null,
      done:   new Set(),
      rejected: false,
      subCtaKey: iAmCreator ? 'pipeline.subcta.creatorWaiting' : '',
    }
  }
  const phase  = acceptance.effective_phase ?? acceptance.phase
  const cpName = acceptance.counterparty.displayName

  // Photo-proof flow - international (auto-approved at take-on, no
  // 'pending') AND local-with-photo_proof (still 'pending' first since
  // the creator vets the request). From 'accepted' onwards the two
  // branches are identical: no date concertation, no meet-up step.
  if (usesPhotoProof) {
    if (phase === 'pending') {
      return {
        active: 'accept',
        done: new Set(),
        rejected: false,
        subCtaKey: iAmCreator
          ? 'pipeline.subcta.creatorReviewPending'
          : 'pipeline.subcta.acceptorAwaitingReview',
        subCtaName: cpName,
      }
    }
    if (phase === 'accepted') {
      return {
        active: 'proof',
        done: new Set(['accept']),
        rejected: false,
        subCtaKey: iAmCreator ? 'pipeline.subcta.waitingForProof' : 'pipeline.subcta.submitProof',
      }
    }
    if (phase === 'proof_submitted') {
      return {
        active: 'verdict',
        done: new Set(['accept', 'proof']),
        rejected: false,
        subCtaKey: iAmCreator ? 'pipeline.subcta.reviewProof' : 'pipeline.subcta.waitingForVerdict',
      }
    }
    if (phase === 'approved') {
      return { active: null, done: new Set(['accept', 'proof', 'verdict']), rejected: false, subCtaKey: 'pipeline.subcta.accomplished' }
    }
    return { active: null, done: new Set(['accept', 'proof']), rejected: true, subCtaKey: 'pipeline.subcta.closed' }
  }

  // PR5 - pending = creator hasn't reviewed the take-on request yet.
  if (phase === 'pending') {
    return {
      active: 'accept',
      done: new Set(),
      rejected: false,
      subCtaKey: iAmCreator
        ? 'pipeline.subcta.creatorReviewPending'
        : 'pipeline.subcta.acceptorAwaitingReview',
      subCtaName: cpName,
    }
  }

  if (phase === 'accepted') {
    const hasProposal = acceptance.proposed_starts_at != null
    // The OTHER party - whoever did NOT propose - sees "Approve date".
    // The proposer sees "Awaiting their approval". Mirrors the
    // ScheduleBlock fix so the pipeline label and the actual button
    // stay in sync regardless of who proposed.
    const iProposed = hasProposal && !!myUserId && acceptance.proposed_by_user_id === myUserId
    return {
      active: 'date',
      done:   new Set(['accept']),
      rejected: false,
      subCtaKey: hasProposal
        ? (iProposed ? 'pipeline.subcta.dateAwaiting' : 'pipeline.subcta.approveDate')
        : 'pipeline.subcta.proposeDate',
    }
  }
  if (phase === 'scheduled') {
    return {
      active: 'meet',
      done: new Set(['accept', 'date']),
      rejected: false,
      subCtaKey: 'pipeline.subcta.meetSoon',
      subCtaDate: acceptance.proposed_starts_at ? formatMeetupDate(acceptance.proposed_starts_at, locale) : undefined,
    }
  }
  if (phase === 'debrief') {
    // PR6 - mutual rating. Both sides see the same prompt; the rate-prompt
    // banner on /threads (B.3) owns the urgency copy via `other_rated`.
    return {
      active: 'wrap',
      done:   new Set(['accept', 'date', 'meet']),
      rejected: false,
      subCtaKey: 'pipeline.subcta.rateMeetup',
      subCtaName: cpName,
    }
  }
  if (phase === 'approved') {
    return { active: null, done: new Set(['accept', 'date', 'meet', 'wrap']), rejected: false, subCtaKey: 'pipeline.subcta.accomplished' }
  }
  return { active: null, done: new Set(['accept', 'date', 'meet']), rejected: true, subCtaKey: 'pipeline.subcta.closed' }
}

export default function ChallengePipeline({ acceptance, iAmCreator, myUserId = null, mode = 'local', validationMethod = 'meet', onClick }) {
  const { t, i18n } = useTranslation('challenge')
  const isInternational = mode === 'international'
  const usesPhotoProof  = isInternational || validationMethod === 'photo_proof'
  const STEPS = usesPhotoProof ? STEPS_INTERNATIONAL : STEPS_LOCAL
  const REJECT_STEP = usesPhotoProof ? 'verdict' : 'wrap'
  const state   = derive(acceptance, iAmCreator, myUserId, i18n.language, usesPhotoProof)
  const interactive = !!onClick && !!acceptance

  return (
    <div
      onClick={interactive ? onClick : undefined}
      style={{
        padding: '8px 16px 10px',
        cursor: interactive ? 'pointer' : 'default',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      {/* Dots row */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {STEPS.map((step, i) => {
          const isDone   = state.done.has(step)
          const isActive = state.active === step
          const isReject = state.rejected && step === REJECT_STEP
          const connectorLit = state.done.has(STEPS[i - 1]) || state.active === step
          return (
            <div key={step} style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Connector from prev dot - inset by DOT_RADIUS so the line
                  stops at each dot's edge instead of crossing through. The
                  dot is 30px wide, so half-width = 15. */}
              {i > 0 && (
                <div style={{
                  position: 'absolute', top: 14, left: '-50%', right: '50%', height: 2,
                  marginLeft: 15, marginRight: 15,
                  background: connectorLit ? 'var(--primary)' : 'var(--border)',
                }} />
              )}
              <div style={{
                width: 30, height: 30, borderRadius: 15,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px solid',
                ...(isReject ? {
                  background: 'var(--surface2, #1f1a17)', borderColor: 'var(--overlay-strong)', color: 'var(--muted, #b3b3b3)',
                } : isDone ? {
                  background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff',
                } : isActive ? {
                  background: 'var(--primary-tint)', borderColor: 'var(--primary)', color: 'var(--primary-text)',
                } : {
                  background: 'var(--surface2, #1f1a17)', borderColor: 'var(--border)', color: 'var(--muted, #b3b3b3)',
                }),
                position: 'relative', zIndex: 1, fontSize: 14,
              }}>
                {isDone ? <Icon name="checkmark" size={14} /> : <Icon name={ICONS[step]} size={14} style={{ opacity: isActive ? 1 : 0.45 }} />}
              </div>
              <span style={{
                fontSize: 10, fontWeight: isActive ? 800 : isDone ? 700 : 600,
                color: isActive ? 'var(--primary-text)' : isDone ? 'var(--text, #fff)' : 'var(--muted, #b3b3b3)',
                marginTop: 4, letterSpacing: 0.2,
              }}>
                {t(`pipeline.step.${step}`)}
              </span>
            </div>
          )
        })}
      </div>

      {/* Sub-CTA - empty key suppresses the row (visitor with no acceptance:
          the labeled accept button below handles the call to action). */}
      {!!state.subCtaKey && (
        <div style={{
          alignSelf: 'center',
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 12px', borderRadius: 999,
          background: 'var(--primary-tint)',
          border: '1px solid var(--line)',
        }}>
          <span style={{ color: 'var(--primary-text)', fontWeight: 700, fontSize: 12 }}>
            {t(state.subCtaKey, {
              ...(state.subCtaName ? { name: state.subCtaName } : {}),
              ...(state.subCtaDate ? { date: state.subCtaDate } : {}),
            })}
          </span>
          {interactive && <Icon name="chevron-forward" size={12} style={{ color: 'var(--primary-text)' }} />}
        </div>
      )}
    </div>
  )
}
